import React, { useEffect } from 'react';
import { Box, Typography, LinearProgress, Button, Alert, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VideocamIcon from '@mui/icons-material/Videocam';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export type UploadStatus = 'idle' | 'preparing' | 'uploading' | 'processing' | 'success' | 'error';

interface VideoUploadProgressProps {
  status: UploadStatus;
  progress?: number;
  errorMessage?: string;
  onRetry?: () => void;
  isOnline?: boolean;
}

/**
 * Componente para exibir o progresso de upload de vídeo com feedback visual
 */
const VideoUploadProgress: React.FC<VideoUploadProgressProps> = ({
  status,
  progress = 0,
  errorMessage,
  onRetry,
  isOnline = navigator.onLine
}) => {
  // Efeito para vibrar quando o status mudar (em dispositivos móveis)
  useEffect(() => {
    if (status === 'success' || status === 'error') {
      try {
        if (navigator.vibrate) {
          navigator.vibrate(200);
        }
      } catch (e) {
        // Ignora se a vibração não for suportada
      }
    }
  }, [status]);

  return (
    <Box sx={{ width: '100%', my: 2 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 1, 
          color: status === 'error' ? 'error.main' : 'primary.main' 
        }}
      >
        {status === 'idle' && <VideocamIcon sx={{ mr: 1 }} />}
        {status === 'preparing' && <CircularProgress size={20} sx={{ mr: 1 }} />}
        {status === 'uploading' && <CloudUploadIcon sx={{ mr: 1 }} />}
        {status === 'processing' && <CircularProgress size={20} sx={{ mr: 1 }} />}
        {status === 'success' && <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />}
        {status === 'error' && <ErrorIcon sx={{ mr: 1, color: 'error.main' }} />}
        
        <Typography variant="body2" fontWeight="medium">
          {status === 'idle' && 'Pronto para enviar o vídeo'}
          {status === 'preparing' && 'Preparando vídeo...'}
          {status === 'uploading' && `Enviando vídeo (${Math.round(progress)}%)...`}
          {status === 'processing' && 'Processando seu vídeo...'}
          {status === 'success' && 'Vídeo enviado com sucesso!'}
          {status === 'error' && 'Erro no envio do vídeo'}
        </Typography>
      </Box>
      
      {(status === 'uploading' || status === 'processing') && (
        <Box sx={{ position: 'relative', pt: 0.5 }}>
          {status === 'uploading' ? (
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                '& .MuiLinearProgress-bar': {
                  transition: 'transform 0.1s linear'
                }
              }} 
            />
          ) : (
            <LinearProgress 
              variant="indeterminate" 
              sx={{ height: 8, borderRadius: 4 }} 
            />
          )}
          
          {status === 'uploading' && (
            <Typography 
              variant="caption" 
              sx={{ 
                position: 'absolute', 
                right: 0, 
                top: '100%', 
                mt: 0.5 
              }}
            >
              {Math.round(progress)}%
            </Typography>
          )}
        </Box>
      )}
      
      {status === 'error' && (
        <>
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {errorMessage || 'Ocorreu um erro no envio do vídeo.'}
            <br />
            <Typography variant="caption">
              {!isOnline 
                ? 'Sua conexão está offline. O vídeo foi salvo localmente e enviaremos quando a conexão for restaurada.' 
                : 'Verifique sua conexão e tente novamente.'}
            </Typography>
          </Alert>
          
          {onRetry && (
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />} 
              onClick={onRetry}
              disabled={!isOnline}
              sx={{ mt: 1 }}
              size="small"
            >
              Tentar novamente
            </Button>
          )}
        </>
      )}
      
      {status === 'success' && (
        <Alert severity="success" sx={{ mt: 1 }}>
          Vídeo enviado com sucesso. Continuando para o próximo passo.
        </Alert>
      )}
    </Box>
  );
};

export default VideoUploadProgress; 